import { datadogRum } from '@datadog/browser-rum';
import { isLocalEnvironment } from '~/constants/environment';
import BrowserCookies from './BrowserCookies';
import ErrorWithContext from './ErrorWithContext';
import sanitizeContext from './sanitizeContext';
import toError from './toError';

type Context = Record<string, any>;

/** Informational logging */

function logInfoToConsole(message: string, context: Context) {
  console.log({ message, context });
}

function logInfoToTelemetry(message: string, context: Context) {
  datadogRum.addAction(message, context);
}

export function logInfo(message: string, context: Context = {}) {
  if (isLocalEnvironment) {
    logInfoToConsole(message, context);
  } else {
    logInfoToTelemetry(message, sanitizeContext(context));
  }
}

/** Error logging */

type ErrorWithResponse = {
  response?: {
    data?: string;
  };
};

export type LogErrorOptions = {
  callback?: (_: Error) => void;
  controller?: AbortController;
};

function logErrorToConsole(error: Error, context?: Context) {
  console.error({
    message: error.message,
    stack: error.stack,
    context
  });
}

function logErrorToTelemetry(error: Error, context?: Context) {
  datadogRum.addError(error, context);
}

export function isErrorWithResponse(err: unknown): err is ErrorWithResponse {
  return typeof err === 'object' && err !== null && 'response' in err;
}

export function logError(
  err: unknown,
  context: Context = {},
  options: LogErrorOptions = {}
) {
  // ErrorWithContext carries its own context, set at throw/construction time.
  // Unwrap it here so that context survives even if a caller passes the
  // wrapper straight through (e.g. an ErrorBoundary catching a thrown
  // ErrorWithContext) instead of spreading .context into the second argument.
  const isWrapped = err instanceof ErrorWithContext;
  const error = toError(isWrapped ? err.error : err);
  const mergedContext = isWrapped ? { ...err.context, ...context } : context;
  const errContext = isWrapped ? error : err;
  const { callback, controller } = options;

  // Log aborts as info instead of errors
  if (controller?.signal?.aborted) {
    logInfo('Request aborted', {
      ...mergedContext,
      reason: controller.signal.reason
    });

    return;
  }

  if (isLocalEnvironment) {
    logErrorToConsole(error, { err: errContext, ...mergedContext });
  } else {
    logErrorToTelemetry(
      error,
      sanitizeContext({ err: errContext, ...mergedContext })
    );
  }

  if (callback) callback(error);
}

export const generateErrorLogger =
  (location: string, globalContext?: Context, options?: LogErrorOptions) =>
  (err: unknown, instanceContext?: Context) =>
    logError(
      err,
      {
        location,
        ...globalContext,
        ...instanceContext,
        BrowserCookieId: BrowserCookies.get('BrowserCookieId') || '',
        fullStoryId: BrowserCookies.get('fs_uid') || '',
        url: window.location.href
      },
      options
    );
